import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUserPlus, FaArrowLeft, FaSpinner, FaEnvelope, FaPhone, FaUser, FaCheckCircle, FaInfoCircle, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddAdvisor = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [managers, setManagers] = useState([]);
  const [createdAdvisor, setCreatedAdvisor] = useState(null);
  
  // Form data - different fields for super_admin vs manager
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    manager_id: '',
    notes: ''
  });

  const isSuperAdmin = user?.role === 'super_admin';

  // Fetch managers list for super admin
  useEffect(() => {
    if (isSuperAdmin) {
      fetchManagers();
    }
  }, [isSuperAdmin]);

  const fetchManagers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/managers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setManagers(response.data || []);
    } catch (err) {
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, password }));
  };

  // Super Admin: Direct creation
  const handleSuperAdminSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields (Name, Email, Password)');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/advisors`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        manager_id: formData.manager_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCreatedAdvisor({
        name: formData.name,
        email: formData.email,
        password: formData.password
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create advisor. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Manager: Request submission
  const handleManagerSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/manager/request-advisor`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success screen for Super Admin
  if (success && isSuperAdmin && createdAdvisor) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-green-400 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Advisor Created Successfully!</h2>
          <p className="text-gray-300 mb-6">
            <span className="text-[#FFD700] font-bold">{createdAdvisor.name}</span> has been added as an advisor.
          </p>
          
          {/* Credentials Box */}
          <div className="bg-[#0F172A] rounded-xl p-6 mb-6 text-left">
            <h3 className="text-lg font-bold text-[#FFD700] mb-4">Login Credentials</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-[#1A2942] rounded-lg">
                <span className="text-gray-400">Email (User ID):</span>
                <span className="text-white font-mono">{createdAdvisor.email}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-[#1A2942] rounded-lg">
                <span className="text-gray-400">Password:</span>
                <span className="text-[#FFD700] font-mono">{createdAdvisor.password}</span>
              </div>
            </div>
            <p className="text-orange-400 text-sm mt-4">
              <FaInfoCircle className="inline mr-2" />
              Please share these credentials securely with the advisor. They will be prompted to change their password on first login.
            </p>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Success screen for Manager
  if (success && !isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl text-center">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <FaCheckCircle className="text-green-400 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">Request Submitted Successfully!</h2>
          <p className="text-gray-300 mb-4">
            Your request to add <span className="text-[#FFD700] font-bold">{formData.name}</span> as an advisor has been submitted.
          </p>
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-6">
            <p className="text-blue-400 text-sm">
              <FaInfoCircle className="inline mr-2" />
              The Super Admin will review this request and issue login credentials once approved.
              You will be notified when the advisor is ready to join your team.
            </p>
          </div>
          <p className="text-gray-400 text-sm">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center space-x-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 text-gray-400 hover:text-white transition-colors"
        >
          <FaArrowLeft size={24} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isSuperAdmin ? 'Add New Advisor' : 'Request New Advisor'}
          </h1>
          <p className="text-gray-400">
            {isSuperAdmin 
              ? 'Create a new advisor account with login credentials' 
              : 'Submit a request to add an advisor to your team'}
          </p>
        </div>
      </div>

      {/* Info Banner - Only for Manager */}
      {!isSuperAdmin && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
          <div className="flex items-start space-x-3">
            <FaInfoCircle className="text-blue-400 text-xl mt-0.5" />
            <div>
              <p className="text-blue-400 font-bold">How it works:</p>
              <ul className="text-gray-300 text-sm mt-2 space-y-1">
                <li>1. Submit the advisor's details below</li>
                <li>2. Super Admin reviews and approves the request</li>
                <li>3. Super Admin creates login credentials for the advisor</li>
                <li>4. The advisor is added to your team once approved</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-gradient-to-br from-[#1A2942] to-[#1E3A5F] rounded-xl p-8 border border-[#2D5A8F] shadow-xl">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
            <FaUserPlus className="text-blue-400 text-xl" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Advisor Information</h2>
            <p className="text-gray-400 text-sm">
              {isSuperAdmin ? 'Enter the advisor details and set login credentials' : 'Fill in the details of the advisor you want to add'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400">
            {error}
          </div>
        )}

        <form onSubmit={isSuperAdmin ? handleSuperAdminSubmit : handleManagerSubmit} className="space-y-6">
          {/* Name */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Full Name *
            </label>
            <div className="relative">
              <FaUser className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter advisor's full name"
                className="w-full pl-12 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                required
                data-testid="advisor-name-input"
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Email Address (User ID) *
            </label>
            <div className="relative">
              <FaEnvelope className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="advisor@example.com"
                className="w-full pl-12 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                required
                data-testid="advisor-email-input"
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-gray-300 text-sm font-medium mb-2">
              Phone Number
            </label>
            <div className="relative">
              <FaPhone className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 9876543210"
                className="w-full pl-12 pr-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                data-testid="advisor-phone-input"
              />
            </div>
          </div>

          {/* Password - Only for Super Admin */}
          {isSuperAdmin && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Password *
              </label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Enter password"
                  className="w-full pl-12 pr-24 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none"
                  required
                  data-testid="advisor-password-input"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                  <button
                    type="button"
                    onClick={generatePassword}
                    className="px-2 py-1 text-xs bg-[#2D5A8F] text-white rounded hover:bg-[#3D6A9F]"
                  >
                    Generate
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Manager Selection - Only for Super Admin */}
          {isSuperAdmin && managers.length > 0 && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Assign to Manager (Optional)
              </label>
              <select
                name="manager_id"
                value={formData.manager_id}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white focus:border-[#FFD700] focus:outline-none"
                data-testid="advisor-manager-select"
              >
                <option value="">No Manager (Independent Advisor)</option>
                {managers.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.name} ({manager.email})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Notes - Only for Manager */}
          {!isSuperAdmin && (
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Notes for Admin (Optional)
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any additional information about this advisor..."
                rows={3}
                className="w-full px-4 py-3 bg-[#0F172A] border border-[#2D5A8F] rounded-lg text-white placeholder-gray-500 focus:border-[#FFD700] focus:outline-none resize-none"
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="flex-1 py-3 bg-[#1E3A5F] text-white font-medium rounded-lg hover:bg-[#2D5A8F] transition-all border border-[#2D5A8F]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 py-3 bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A1929] font-bold rounded-lg hover:from-[#FFD700] hover:to-[#D4AF37] transition-all flex items-center justify-center space-x-2"
              data-testid="submit-advisor-btn"
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  <span>{isSuperAdmin ? 'Creating...' : 'Submitting...'}</span>
                </>
              ) : (
                <>
                  <FaUserPlus />
                  <span>{isSuperAdmin ? 'Create Advisor' : 'Submit Request'}</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Note */}
        {!isSuperAdmin && (
          <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-yellow-400 text-sm">
              <strong>Note:</strong> Only Super Admin can approve advisor requests and issue login credentials. 
              You will not be able to set or change the advisor's password.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddAdvisor;
